<template>
  <wui-modal
    v-click-outside="closeDialog"
    :title="$t('chat.sendForwardTitle', 1)"
    :size="40"
    class="flex flex-col !max-h-[100dvh] !h-[min(850px,100dvh)] !w-[430px] short:!rounded-none phone:!rounded-none short:!h-screen phone:!w-screen [&_.wui-modal__content]:min-h-0 [&_.wui-modal__content]:flex-1 [&_.wui-modal__content]:flex [&_.wui-modal__content]:flex-col [&_.wui-modal__content]:overflow-hidden"
    @close="closeDialog"
  >
    <main class="flex min-h-0 flex-1 flex-col gap-y-2">
      <chat-forward-message-input
        class="shrink-0"
        :events="localChatEvents"
        :files="(attachedFilesList as unknown as (File | ChatFile)[])"
        @input="onInput"
        @add-files="onAddFiles"
        @remove-file="onRemoveFile"
      />
      <chat-forward-message-list
        v-model="selectedContacts"
        class="min-h-0 flex-1"
        :source-p-served="firstEvent?.item.pServed"
        :loading="loading!"
      />
    </main>
    <template #footer>
      <chat-forward-message-send-btn
        :chat-events="localChatEvents"
        :selected-contacts="selectedContacts"
        :message-text="messageText"
        :chat-files="(attachedFilesList as unknown as ChatFile[])"
        :send-disabled="!canSend"
        @sent="afterForward?.()"
      />
    </template>
  </wui-modal>
</template>

<script setup lang="ts">
import { ClickOutside as vClickOutside, closeDialogKey, safeInject, WuiModal } from '@wui/common-library'
import { HistoryMessageItem, MessageIncomingEvent, MessageOutgoingEvent, PServed } from '@wui/im'
import { ref, toRefs, computed, unref, Ref } from 'vue'

import { useFilesUpload } from '@/features/chat-files-upload'
import { ChatForwardMessageSendBtn } from '@/features/chat-forward-message-btn'
import { ChatForwardMessageInput } from '@/features/chat-forward-message-input'
import { ChatForwardMessageList } from '@/features/chat-forward-message-list'

import { usePromptServer } from '@/shared/composables/web-dav'
import { ChatFile } from '@/shared/types/files'
import { convertUploadingFiles } from '@/shared/utils/convert-uploading-files'
import { enrichChatFilesWithMediaDimensions } from '@/shared/utils/files'

const props = defineProps<{
  chatEvents: HistoryMessageItem<MessageIncomingEvent | MessageOutgoingEvent>[]
  loading?: boolean
  selectedText?: string
  afterForward?: () => void
}>()

const FORWARD_ROOM_ID = 'forward'
const closeModal = safeInject(closeDialogKey)
const { removeFile, abortSaveFile } = usePromptServer()
const { chatEvents, selectedText } = toRefs(props)

const selectedContacts = ref<PServed[]>([])
const messageText = ref('')
const attachedFiles = ref<ChatFile[]>([])
const localChatEvents = ref(JSON.parse(JSON.stringify(chatEvents.value)))

const firstEvent = computed(() => Array.isArray(localChatEvents.value) ? localChatEvents.value.at(0) : localChatEvents.value)

if (selectedText.value && localChatEvents.value.length === 1) {
  localChatEvents.value.at(0).item.messageText = selectedText.value
}

const fileListFromFiles = (files: File[]): FileList => {
  const dt = new DataTransfer()
  files.forEach(f => dt.items.add(f))
  return dt.files
}

const removeUploadedFileFromServer = async (file: { name: string; file: File; isLoaded: Ref<boolean> | boolean; isError: Ref<boolean> | boolean }) => {
  if (unref(file.isLoaded)) {
    await removeFile(file.name)
  } else if (!unref(file.isError)) {
    abortSaveFile(file.file)
  }
}

const attachedFilesList = computed(() => attachedFiles.value)

const allFilesUploaded = computed(() =>
  attachedFiles.value.length === 0 ||
  attachedFiles.value.every(f => unref(f.isLoaded) || unref(f.isError)),
)
const canSend = computed(() => attachedFiles.value.length === 0 || allFilesUploaded.value)

const onInput = (message: string) => {
  messageText.value = message
}

const onAddFiles = async (files: File[]) => {
  if (!files.length) return
  const newChatFiles = convertUploadingFiles(fileListFromFiles(files), FORWARD_ROOM_ID)
  await enrichChatFilesWithMediaDimensions(newChatFiles)
  attachedFiles.value = [...attachedFiles.value, ...newChatFiles] as ChatFile[]
  await useFilesUpload().uploadFiles(newChatFiles)
}

const onRemoveFile = async (index: number) => {
  const file = attachedFiles.value[index]
  if (file) {
    await removeUploadedFileFromServer(file)
  }
  attachedFiles.value = attachedFiles.value.filter((_, i) => i !== index)
}

const closeDialog = async () => {
  await Promise.all(
    attachedFiles.value.map(file => removeUploadedFileFromServer(file)),
  )
  attachedFiles.value = []
  closeModal(undefined)
}
</script>
